import { FINANCIAL_COLORS } from '@components/charts/chartConstants';
import type {
  BalanceSheetApiItem,
  CashFlowApiItem,
  FullYearForecastType,
  FullYearResultsApiItem,
  QuarterlyResultsApiItem,
  QuarterlyResultsForecastType,
  ResearchDevelopmentApiItem,
} from '@app-types/performanceTrendsApi';
import type {
  DisplayLine,
  FinancialSection,
  FinancialSeries,
  GroupedFinancialChartData,
  MatrixTableColumn,
  MatrixTableRow,
  PerformanceHistoryChartData,
  PerformanceHistorySection,
  QuarterKey,
  QuarterlyPeriodPoint,
  QuarterlyPerformanceSectionData,
  QuarterlyPeriodChartData,
  ValueTone,
} from '@app-types/performanceTrends';
import { formatNumber, formatPercent, formatSigned, PLACEHOLDER } from '@utils/format';

const AMOUNT_FRACTION_DIGITS = 2;
const RATIO_FRACTION_DIGITS = 2;
const YOY_FRACTION_DIGITS = 2;

/** Nice-number ladder for the y-axis top value; each entry divides into 4 clean steps. */
const AXIS_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
const AXIS_TICK_COUNT = 4;
const FALLBACK_AXIS_MAX = 100;

const UNIT_SCALE_LABELS: Record<string, string> = {
  K: '千',
  M: '百万',
  B: '十億',
  T: '兆',
};

const CURRENCY_LABELS: Record<string, string> = {
  JPY: '円',
  USD: 'ドル',
};

const UNIT_LABEL_PREFIX = '単位:';

const line = (text: string, tone: ValueTone = 'default'): DisplayLine => ({ text, tone });

/** Reads a numeric API field, treating non-finite payloads as missing. */
const numericField = <T, K extends keyof T>(
  row: T,
  field: K,
): number | null => {
  const raw = row[field];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

/** `2026/02/13 03:30:00` -> `2026/02/13`; also tolerates ISO `-`/`T` separators. */
export const formatAnnounceDate = (value: string | null | undefined): string => {
  const match = value?.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (!match) return PLACEHOLDER;
  const [, year, month, day] = match;
  return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
};

/** `2021/12/31` -> `2021`, used for the chart x-axis. */
export const formatPeriodChartLabel = (value: string | null | undefined): string => {
  const match = value?.trim().match(/^(\d{4})/);
  return match ? match[1] : PLACEHOLDER;
};

/** Digits-only sort key so `2021/12/31` and `2021-12-31` compare identically. */
const periodSortKey = (value: string | null | undefined): string =>
  value ? value.trim().replace(/[^\d]/g, '') : '';

type PeriodRow = {
  periodEndDate: string | null | undefined;
};

/** Oldest -> newest; rows without a period end date are pushed to the end. */
const sortByPeriodAscending = <T extends PeriodRow>(rows: T[]): T[] =>
  rows.slice().sort((a, b) => {
    const left = periodSortKey(a.periodEndDate);
    const right = periodSortKey(b.periodEndDate);
    if (!left) return right ? 1 : 0;
    if (!right) return -1;
    return left.localeCompare(right);
  });

/** Newest -> oldest for BS/CF tables only; missing keys stay last, ties stable. */
const sortByPeriodDescending = <T extends PeriodRow>(rows: T[]): T[] =>
  rows.slice().sort((a, b) => {
    const left = periodSortKey(a.periodEndDate);
    const right = periodSortKey(b.periodEndDate);
    if (!left) return right ? 1 : 0;
    if (!right) return -1;
    return right.localeCompare(left);
  });

/**
 * `(+5.91)` / `(-8.45)` / `(0.00)` with Summary-style tone, or `--` when the
 * backend has no prior period to compare against. YoY values arrive from the
 * API already expressed in percent, so they are never rescaled here.
 */
const buildYoyLine = (value: number | null): DisplayLine => {
  if (value === null) return line(PLACEHOLDER);
  const tone: ValueTone = value > 0 ? 'positive' : value < 0 ? 'negative' : 'default';
  return line(`(${formatSigned(value, YOY_FRACTION_DIGITS)})`, tone);
};

type BalanceSheetValueKind = 'amount' | 'ratio' | 'date';

interface BalanceSheetColumnSpec {
  key: string;
  label: string;
  kind: BalanceSheetValueKind;
  valueField: keyof BalanceSheetApiItem;
  yoyField?: keyof BalanceSheetApiItem;
}

/**
 * The confirmed 貸借対照表 column set, in display order. Columns and cells are
 * both derived from this list so they cannot drift apart. `bps` is deliberately
 * absent: the API returns it but the spec does not show it.
 */
export const BALANCE_SHEET_COLUMN_SPECS: BalanceSheetColumnSpec[] = [
  { key: 'totalAssets', label: '総資産', kind: 'amount', valueField: 'totalAssets', yoyField: 'totalAssetsYoy' },
  { key: 'currentAssets', label: '流動資産', kind: 'amount', valueField: 'currentAssets', yoyField: 'currentAssetsYoy' },
  { key: 'currentLiabilities', label: '流動負債', kind: 'amount', valueField: 'currentLiabilities', yoyField: 'currentLiabilitiesYoy' },
  { key: 'longTermDebt', label: '固定負債', kind: 'amount', valueField: 'longTermDebt', yoyField: 'longTermDebtYoy' },
  { key: 'shareholderEquity', label: '自己資本', kind: 'amount', valueField: 'shareholderEquity', yoyField: 'shareholderEquityYoy' },
  { key: 'equityRatio', label: '自己資本比率', kind: 'ratio', valueField: 'equityRatio', yoyField: 'equityRatioYoy' },
  { key: 'roe', label: '株主資本利益率', kind: 'ratio', valueField: 'roe', yoyField: 'roeYoy' },
  { key: 'roa', label: 'ROA', kind: 'ratio', valueField: 'roa', yoyField: 'roaYoy' },
  { key: 'announceDate', label: '決算発表日', kind: 'date', valueField: 'announceDate' },
];

/** Series shown in the grouped bar chart (総資産 + 固定負債 only, per spec). */
const BALANCE_SHEET_CHART_SPECS = [
  { key: 'total-assets', label: '総資産', color: FINANCIAL_COLORS.red, field: 'totalAssets' as const },
  { key: 'long-term-debt', label: '固定負債', color: FINANCIAL_COLORS.blue, field: 'longTermDebt' as const },
];

/** `単位:百万ドル` / `単位:百万円`, taken from the first row that reports them. */
export const buildUnitLabel = (rows: BalanceSheetApiItem[]): string | undefined => {
  const source = rows.find((row) => row.unit || row.currency);
  if (!source) return undefined;

  const scale = source.unit
    ? UNIT_SCALE_LABELS[source.unit.trim().toUpperCase()] ?? source.unit.trim()
    : '';
  const currency = source.currency
    ? CURRENCY_LABELS[source.currency.trim().toUpperCase()] ?? source.currency.trim()
    : '';

  const text = `${scale}${currency}`;
  return text ? `単位:${text}` : undefined;
};

/**
 * Rounds the series peak up to a nice value and splits it into 4 equal ticks,
 * replacing the hard-coded mock scale. Returns a neutral scale when there is no
 * usable data so the empty chart still renders an axis.
 */
const roundAxisPeak = (peak: number): number => {
  if (peak <= 0) return FALLBACK_AXIS_MAX;

  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = AXIS_STEPS.find((candidate) => magnitude * candidate >= peak) ?? 10;
  return magnitude * step;
};

export const buildAxisScale = (
  values: Array<number | null>,
): { max: number; interval: number } => {
  const peak = values.reduce<number>(
    (acc, value) => (typeof value === 'number' && Number.isFinite(value) ? Math.max(acc, value) : acc),
    0,
  );

  if (peak <= 0) {
    return { max: FALLBACK_AXIS_MAX, interval: FALLBACK_AXIS_MAX / AXIS_TICK_COUNT };
  }

  const max = roundAxisPeak(peak);

  return { max, interval: max / AXIS_TICK_COUNT };
};

export const buildSignedAxisScale = (
  values: Array<number | null>,
): { min: number; max: number; interval: number } => {
  const finiteValues = values.filter((value): value is number => (
    typeof value === 'number' && Number.isFinite(value)
  ));

  if (finiteValues.length === 0) {
    return {
      min: -FALLBACK_AXIS_MAX,
      max: FALLBACK_AXIS_MAX,
      interval: (FALLBACK_AXIS_MAX * 2) / AXIS_TICK_COUNT,
    };
  }

  const minValue = Math.min(...finiteValues);
  const maxValue = Math.max(...finiteValues);

  if (minValue < 0 && maxValue > 0) {
    const bound = roundAxisPeak(Math.max(Math.abs(minValue), Math.abs(maxValue)));
    return {
      min: -bound,
      max: bound,
      interval: (bound * 2) / AXIS_TICK_COUNT,
    };
  }

  if (maxValue <= 0) {
    const min = -roundAxisPeak(Math.abs(minValue));
    return {
      min,
      max: 0,
      interval: Math.abs(min) / AXIS_TICK_COUNT,
    };
  }

  const max = roundAxisPeak(maxValue);
  return {
    min: 0,
    max,
    interval: max / AXIS_TICK_COUNT,
  };
};

const buildChart = (
  rows: BalanceSheetApiItem[],
  chartKey: string,
): { chart: GroupedFinancialChartData; series: FinancialSeries[] } => {
  const series: FinancialSeries[] = BALANCE_SHEET_CHART_SPECS.map((spec) => ({
    key: spec.key,
    label: spec.label,
    color: spec.color,
    values: rows.map((row) => numericField(row, spec.field)),
  }));

  const { max, interval } = buildAxisScale(series.flatMap((s) => s.values));

  return {
    series,
    chart: {
      key: chartKey,
      labels: rows.map((row) => formatPeriodChartLabel(row.periodEndDate)),
      yAxisMin: 0,
      yAxisMax: max,
      yAxisInterval: interval,
      series,
    },
  };
};

const buildColumns = (rows: BalanceSheetApiItem[]): MatrixTableColumn[] => {
  const unitLabel = buildUnitLabel(rows);
  const labelColumn: MatrixTableColumn = {
    key: 'label',
    label: unitLabel ? [line('決算期'), line(unitLabel)] : [line('決算期')],
    align: 'left',
  };

  return [
    labelColumn,
    ...BALANCE_SHEET_COLUMN_SPECS.map<MatrixTableColumn>((spec) => ({
      key: spec.key,
      label: [line(spec.label)],
      align: 'right',
    })),
  ];
};

const buildCell = (row: BalanceSheetApiItem, spec: BalanceSheetColumnSpec): DisplayLine[] => {
  if (spec.kind === 'date') {
    const raw = row[spec.valueField];
    return [line(formatAnnounceDate(typeof raw === 'string' ? raw : null))];
  }

  const value = numericField(row, spec.valueField);
  const text = spec.kind === 'ratio'
    ? formatPercent(value, RATIO_FRACTION_DIGITS)
    : formatNumber(value, AMOUNT_FRACTION_DIGITS);
  const valueLine = line(text);

  return spec.yoyField
    ? [valueLine, buildYoyLine(numericField(row, spec.yoyField))]
    : [valueLine];
};

const buildRows = (rows: BalanceSheetApiItem[]): MatrixTableRow[] =>
  rows.map((row, index) => {
    const period = row.periodEndDate?.trim() || PLACEHOLDER;
    return {
      key: `bs-${periodSortKey(row.periodEndDate) || index}`,
      label: [line(period), line('(前年比)')],
      cells: BALANCE_SHEET_COLUMN_SPECS.map((spec) => buildCell(row, spec)),
    };
  });

type FullYearValueField =
  | 'revenue'
  | 'operatingProfit'
  | 'recurringProfit'
  | 'netProfit'
  | 'eps'
  | 'bps';

type FullYearYoyField =
  | 'revenueYoy'
  | 'operatingProfitYoy'
  | 'recurringProfitYoy'
  | 'netProfitYoy'
  | 'epsYoy'
  | 'bpsYoy';

interface FullYearTableSpec {
  key: string;
  valueField: FullYearValueField;
  yoyField: FullYearYoyField;
}

interface FullYearChartSpec {
  valueField: Extract<
    FullYearValueField,
    'revenue' | 'operatingProfit' | 'recurringProfit' | 'netProfit'
  >;
}

const FULL_YEAR_TABLE_SPECS: FullYearTableSpec[] = [
  { key: 'revenue', valueField: 'revenue', yoyField: 'revenueYoy' },
  { key: 'operating', valueField: 'operatingProfit', yoyField: 'operatingProfitYoy' },
  { key: 'ordinary', valueField: 'recurringProfit', yoyField: 'recurringProfitYoy' },
  { key: 'net', valueField: 'netProfit', yoyField: 'netProfitYoy' },
  { key: 'eps', valueField: 'eps', yoyField: 'epsYoy' },
  { key: 'bps', valueField: 'bps', yoyField: 'bpsYoy' },
];

const FULL_YEAR_CHART_SPECS: FullYearChartSpec[] = [
  { valueField: 'revenue' },
  { valueField: 'operatingProfit' },
  { valueField: 'recurringProfit' },
  { valueField: 'netProfit' },
];

const FULL_YEAR_FORECAST_MARKER = '予';

const numericFullYearField = (
  row: FullYearResultsApiItem,
  field: FullYearValueField | FullYearYoyField,
): number | null => {
  const raw = row[field];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

const normalizeSource = (value: string | null | undefined): string =>
  value?.trim().toLowerCase() ?? '';

const normalizeMetadataValue = (value: string | null | undefined): string =>
  value?.trim().toUpperCase() ?? '';

const hasFullYearDisplayValue = (row: FullYearResultsApiItem): boolean =>
  FULL_YEAR_TABLE_SPECS.some((spec) => numericFullYearField(row, spec.valueField) !== null);

/**
 * Resolves the display unit from the first value-bearing row with complete
 * metadata. Forecast rows may omit currency even when their values share the
 * same response-level scale/currency as actual rows; the display label must
 * not disappear solely for that incomplete metadata.
 */
const buildCompatibleFullYearUnitLabel = (
  rows: FullYearResultsApiItem[],
): string | undefined => {
  const source = rows.find((row) => (
    hasFullYearDisplayValue(row) &&
    Boolean(normalizeMetadataValue(row.unit)) &&
    Boolean(normalizeMetadataValue(row.currency))
  ));
  if (!source) return undefined;

  const unit = normalizeMetadataValue(source.unit);
  const currency = normalizeMetadataValue(source.currency);
  const scale = UNIT_SCALE_LABELS[unit] ?? unit;
  const currencyLabel = CURRENCY_LABELS[currency] ?? currency;
  const text = `${scale}${currencyLabel}`;
  return text ? `${UNIT_LABEL_PREFIX}${text}` : undefined;
};

const unitTextFromLabel = (unitLabel: string | undefined): string | undefined =>
  unitLabel?.startsWith(UNIT_LABEL_PREFIX)
    ? unitLabel.slice(UNIT_LABEL_PREFIX.length)
    : unitLabel;

const stripChartUnit = (title: string): string =>
  title.replace(/\([^)]*\)\s*$/, '');

const buildFullYearChartTitle = (
  fallbackTitle: string,
  unitLabel: string | undefined,
): string => {
  const baseTitle = stripChartUnit(fallbackTitle);
  const unitText = unitTextFromLabel(unitLabel);
  return unitText ? `${baseTitle}(${unitText})` : baseTitle;
};

const formatPeriodMonthLabel = (value: string | null | undefined): string => {
  const match = value?.trim().match(/^(\d{4})[/-](\d{1,2})/);
  if (!match) return PLACEHOLDER;
  const [, year, month] = match;
  return `${year}/${month.padStart(2, '0')}`;
};

const formatPeriodShortMonthLabel = (value: string | null | undefined): string => {
  const match = value?.trim().match(/^(\d{4})[/-](\d{1,2})/);
  if (!match) return PLACEHOLDER;
  const [, year, month] = match;
  return `${year.slice(-2)}/${month.padStart(2, '0')}`;
};

const withForecastMarker = (label: string, isForecast: boolean): string =>
  isForecast && label !== PLACEHOLDER ? `${label}${FULL_YEAR_FORECAST_MARKER}` : label;

const buildFullYearYoyLine = (value: number | null): DisplayLine => {
  if (value === null) return line(PLACEHOLDER);
  const tone: ValueTone = value > 0 ? 'positive' : value < 0 ? 'negative' : 'default';
  return line(`(${formatSigned(value, YOY_FRACTION_DIGITS)}%)`, tone);
};

const buildFullYearCell = (
  row: FullYearResultsApiItem,
  spec: FullYearTableSpec,
): DisplayLine[] => [
  line(formatNumber(numericFullYearField(row, spec.valueField), AMOUNT_FRACTION_DIGITS)),
  buildFullYearYoyLine(numericFullYearField(row, spec.yoyField)),
];

const buildFullYearColumns = (
  fallback: PerformanceHistorySection,
  rows: FullYearResultsApiItem[],
): MatrixTableColumn[] => {
  const unitLabel = buildCompatibleFullYearUnitLabel(rows);
  const [fallbackLabelColumn, ...valueColumns] = fallback.table.columns;
  const labelColumn: MatrixTableColumn = fallbackLabelColumn ?? {
    key: 'label',
    label: [line('決算期')],
    align: 'left',
  };

  return [
    {
      ...labelColumn,
      label: unitLabel ? [line('決算期'), line(unitLabel)] : [line('決算期')],
    },
    ...valueColumns.map((column) => ({ ...column })),
  ];
};

const buildFullYearRows = (
  rows: FullYearResultsApiItem[],
  fallback: PerformanceHistorySection,
): MatrixTableRow[] => {
  const yoyLabel = fallback.table.rows[0]?.label[1]?.text;

  return rows.map((row, index) => {
    const isForecast = normalizeSource(row.source) !== 'std';
    const period = withForecastMarker(formatPeriodMonthLabel(row.periodEndDate), isForecast);
    return {
      key: `fy-${normalizeSource(row.source) || 'unknown'}-${periodSortKey(row.periodEndDate) || index}`,
      label: yoyLabel ? [line(period), line(yoyLabel)] : [line(period)],
      cells: FULL_YEAR_TABLE_SPECS.map((spec) => buildFullYearCell(row, spec)),
    };
  });
};

const buildFullYearCharts = (
  rows: FullYearResultsApiItem[],
  fallback: PerformanceHistorySection,
): PerformanceHistoryChartData[] => {
  const unitLabel = buildCompatibleFullYearUnitLabel(rows);

  return fallback.charts.map((chart, index) => {
    const spec = FULL_YEAR_CHART_SPECS[index];
    if (!spec) {
      return { ...chart, points: [] };
    }

    const points = rows.map((row) => {
      const isForecast = normalizeSource(row.source) !== 'std';
      return {
        label: withForecastMarker(formatPeriodShortMonthLabel(row.periodEndDate), isForecast),
        value: numericFullYearField(row, spec.valueField),
      };
    });
    const values = points.map((point) => point.value);
    // Annual profit values keep their API signs. Only expand below zero when
    // needed; positive, all-zero and no-data charts retain the existing scale.
    const { min, max, interval } = values.some((value) => value !== null && value < 0)
      ? buildSignedAxisScale(values)
      : { min: 0, ...buildAxisScale(values) };

    return {
      ...chart,
      title: buildFullYearChartTitle(chart.title, unitLabel),
      unit: unitTextFromLabel(unitLabel) ?? '',
      min,
      max,
      interval,
      points,
    };
  });
};

const toDisplayCount = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
};

/**
 * Maps GET /api/fundamentals/full-year-results onto the Annual performance
 * history section. Mock data only supplies static presentation metadata; all
 * chart/table values come from the API contract.
 */
export const mapFullYearResultsApiToPerformanceHistorySection = (
  apiData: FullYearResultsApiItem[] | null | undefined,
  fallback: PerformanceHistorySection,
  options: {
    forecastType: FullYearForecastType;
    displayPeriodCount: number;
  },
): PerformanceHistorySection => {
  const rows = Array.isArray(apiData) ? apiData : [];
  const selectedForecastSource = options.forecastType;
  const actualRows = sortByPeriodAscending(
    rows.filter((row) => normalizeSource(row.source) === 'std'),
  );
  const forecastRows = sortByPeriodAscending(
    rows.filter((row) => normalizeSource(row.source) === selectedForecastSource),
  );

  // work_items_87: the Annual display-period option counts actual fiscal
  // periods only (`years=5` -> 5 actual, `years=10` -> 10 actual). Up to one
  // selected estimate period is then added on top, so the maximum displayed
  // total is 6/11. The forecast never consumes an actual-period slot.
  const actualPeriodCount = toDisplayCount(options.displayPeriodCount);
  const actualRowsForDisplay = actualPeriodCount > 0
    ? actualRows.slice(-actualPeriodCount)
    : [];
  // Cap the forecast collection independently at one row. Ascending sort makes
  // `slice(0, 1)` pick the nearest selected forecast period if a malformed
  // response returns more than one.
  const forecastRowsForDisplay = forecastRows.slice(0, 1);
  const chartRows = [...actualRowsForDisplay, ...forecastRowsForDisplay];
  const tableRows = [
    ...forecastRowsForDisplay,
    ...actualRowsForDisplay.slice().reverse(),
  ];

  return {
    ...fallback,
    charts: buildFullYearCharts(chartRows, fallback),
    table: {
      ...fallback.table,
      columns: buildFullYearColumns(fallback, chartRows),
      rows: buildFullYearRows(tableRows, fallback),
    },
  };
};

/**
 * Maps GET /api/fundamentals/balance-sheet onto the 貸借対照表 `FinancialSection`.
 *
 * `fallback` supplies static presentation metadata only (title, Figma node ids,
 * table widths). Every financial value, chart label, and axis bound comes from
 * `apiData`; when it is missing the section renders an empty no-data state
 * instead of falling back to mock numbers.
 */
type CashFlowValueField =
  | 'cashEquivalents'
  | 'operatingCf'
  | 'investmentCf'
  | 'financingCf'
  | 'freeCashFlow'
  | 'perShareOperatingCf';

type CashFlowYoyField =
  | 'cashEquivalentsYoy'
  | 'operatingCfYoy'
  | 'investmentCfYoy'
  | 'financingCfYoy'
  | 'freeCashFlowYoy'
  | 'perShareOperatingCfYoy';

type CashFlowColumnSpec =
  | {
    key: string;
    label: string;
    kind: 'amount' | 'perShare';
    valueField: CashFlowValueField;
    yoyField: CashFlowYoyField;
  }
  | {
    key: string;
    label: string;
    kind: 'date';
    valueField: 'announceDate';
  };

export const CASH_FLOW_COLUMN_SPECS: CashFlowColumnSpec[] = [
  {
    key: 'cashEquivalents',
    label: '現金等期末残高',
    kind: 'amount',
    valueField: 'cashEquivalents',
    yoyField: 'cashEquivalentsYoy',
  },
  {
    key: 'operatingCf',
    label: '営業CF',
    kind: 'amount',
    valueField: 'operatingCf',
    yoyField: 'operatingCfYoy',
  },
  {
    key: 'investmentCf',
    label: '投資CF',
    kind: 'amount',
    valueField: 'investmentCf',
    yoyField: 'investmentCfYoy',
  },
  {
    key: 'financingCf',
    label: '財務CF',
    kind: 'amount',
    valueField: 'financingCf',
    yoyField: 'financingCfYoy',
  },
  {
    key: 'freeCashFlow',
    label: 'フリーCF',
    kind: 'amount',
    valueField: 'freeCashFlow',
    yoyField: 'freeCashFlowYoy',
  },
  {
    key: 'perShareOperatingCf',
    label: '一株営業CF',
    kind: 'perShare',
    valueField: 'perShareOperatingCf',
    yoyField: 'perShareOperatingCfYoy',
  },
  {
    key: 'announceDate',
    label: '決算発表日',
    kind: 'date',
    valueField: 'announceDate',
  },
];

const CASH_FLOW_CHART_SPECS = [
  { key: 'operating-cf', label: '営業CF', color: FINANCIAL_COLORS.red, field: 'operatingCf' as const },
  { key: 'investing-cf', label: '投資CF', color: FINANCIAL_COLORS.blue, field: 'investmentCf' as const },
  { key: 'financing-cf', label: '財務CF', color: FINANCIAL_COLORS.purple, field: 'financingCf' as const },
];

const numericCashFlowField = (
  row: CashFlowApiItem,
  field: CashFlowValueField | CashFlowYoyField,
): number | null => numericField(row, field);

const hasCashFlowDisplayValue = (row: CashFlowApiItem): boolean =>
  CASH_FLOW_COLUMN_SPECS.some((spec) => (
    spec.kind !== 'date' && numericCashFlowField(row, spec.valueField) !== null
  ));

const buildCompatibleCashFlowUnitLabel = (
  rows: CashFlowApiItem[],
): string | undefined => {
  const valueRows = rows.filter(hasCashFlowDisplayValue);
  if (valueRows.length === 0) return undefined;

  const firstUnit = normalizeMetadataValue(valueRows[0].unit);
  const firstCurrency = normalizeMetadataValue(valueRows[0].currency);
  if (!firstUnit || !firstCurrency) return undefined;

  const compatible = valueRows.every((row) => (
    normalizeMetadataValue(row.unit) === firstUnit &&
    normalizeMetadataValue(row.currency) === firstCurrency
  ));
  if (!compatible) return undefined;

  const scale = UNIT_SCALE_LABELS[firstUnit] ?? firstUnit;
  const currency = CURRENCY_LABELS[firstCurrency] ?? firstCurrency;
  const text = `${scale}${currency}`;
  return text ? `${UNIT_LABEL_PREFIX}${text}` : undefined;
};

const buildCashFlowYoyLine = (value: number | null): DisplayLine => {
  if (value === null) return line(PLACEHOLDER);
  const tone: ValueTone = value > 0 ? 'positive' : value < 0 ? 'negative' : 'default';
  return line(`(${formatSigned(value, YOY_FRACTION_DIGITS)}%)`, tone);
};

const buildCashFlowChart = (
  rows: CashFlowApiItem[],
  chartKey: string,
): { chart: GroupedFinancialChartData; series: FinancialSeries[] } => {
  const series: FinancialSeries[] = CASH_FLOW_CHART_SPECS.map((spec) => ({
    key: spec.key,
    label: spec.label,
    color: spec.color,
    values: rows.map((row) => numericField(row, spec.field)),
  }));
  const { min, max, interval } = buildSignedAxisScale(series.flatMap((s) => s.values));

  return {
    series,
    chart: {
      key: chartKey,
      labels: rows.map((row) => formatPeriodChartLabel(row.periodEndDate)),
      yAxisMin: min,
      yAxisMax: max,
      yAxisInterval: interval,
      series,
    },
  };
};

const buildCashFlowColumns = (rows: CashFlowApiItem[]): MatrixTableColumn[] => {
  const unitLabel = buildCompatibleCashFlowUnitLabel(rows);
  const labelColumn: MatrixTableColumn = {
    key: 'label',
    label: unitLabel ? [line('決算期'), line(unitLabel)] : [line('決算期')],
    align: 'left',
  };

  return [
    labelColumn,
    ...CASH_FLOW_COLUMN_SPECS.map<MatrixTableColumn>((spec) => ({
      key: spec.key,
      label: [line(spec.label)],
      align: 'right',
    })),
  ];
};

const buildCashFlowCell = (
  row: CashFlowApiItem,
  spec: CashFlowColumnSpec,
): DisplayLine[] => {
  if (spec.kind === 'date') {
    return [line(formatAnnounceDate(row.announceDate))];
  }

  return [
    line(formatNumber(numericCashFlowField(row, spec.valueField), AMOUNT_FRACTION_DIGITS)),
    buildCashFlowYoyLine(numericCashFlowField(row, spec.yoyField)),
  ];
};

const buildCashFlowRows = (rows: CashFlowApiItem[]): MatrixTableRow[] =>
  rows.map((row, index) => {
    const period = row.periodEndDate?.trim() || PLACEHOLDER;
    return {
      key: `cf-${periodSortKey(row.periodEndDate) || index}`,
      label: [line(period), line('(前年比)')],
      cells: CASH_FLOW_COLUMN_SPECS.map((spec) => buildCashFlowCell(row, spec)),
    };
  });

export const mapBalanceSheetApiToFinancialSection = (
  apiData: BalanceSheetApiItem[] | null | undefined,
  fallback: FinancialSection,
): FinancialSection => {
  const rows = sortByPeriodAscending(Array.isArray(apiData) ? apiData : []);
  const tableRows = sortByPeriodDescending(rows);
  const { chart, series } = buildChart(rows, fallback.chart.key);

  return {
    ...fallback,
    legend: series,
    chart,
    table: {
      ...fallback.table,
      columns: buildColumns(rows),
      rows: buildRows(tableRows),
    },
  };
};

/**
 * Maps GET /api/fundamentals/cash-flow onto the Cash Flow `FinancialSection`.
 *
 * `fallback` supplies static presentation metadata only (title, Figma node ids,
 * table widths). Every financial value, chart label, and axis bound comes from
 * `apiData`; when it is missing the section renders an empty no-data state
 * instead of falling back to mock numbers.
 */
export const mapCashFlowApiToFinancialSection = (
  apiData: CashFlowApiItem[] | null | undefined,
  fallback: FinancialSection,
): FinancialSection => {
  const rows = sortByPeriodAscending(Array.isArray(apiData) ? apiData : []);
  const tableRows = sortByPeriodDescending(rows);
  const { chart, series } = buildCashFlowChart(rows, fallback.chart.key);

  return {
    ...fallback,
    legend: series,
    chart,
    table: {
      ...fallback.table,
      minWidth: Math.max(fallback.table.minWidth ?? 0, 940),
      columns: buildCashFlowColumns(rows),
      rows: buildCashFlowRows(tableRows),
    },
  };
};

type ResearchDevelopmentAmountField =
  | 'depreciationAmortization'
  | 'amortization'
  | 'capex'
  | 'rdCost';

type ResearchDevelopmentRatioField =
  | 'depRevenueRatio'
  | 'capexRevenueRatio'
  | 'rdRevenueRatio';

type ResearchDevelopmentValueField =
  | ResearchDevelopmentAmountField
  | ResearchDevelopmentRatioField;

interface ResearchDevelopmentCellLineSpec {
  field: ResearchDevelopmentValueField;
  kind: 'amount' | 'ratio';
}

interface ResearchDevelopmentRowSpec {
  key: string;
  label: string[];
  cells: ResearchDevelopmentCellLineSpec[];
}

/**
 * Fixed width (px) for each R&D period/data column. Within the requested
 * 100-110px range; fits `YYYY/MM/DD` dates and current signed amounts while
 * keeping the metric-label column at its own `labelColumnWidth` (110px).
 */
export const RESEARCH_DEVELOPMENT_PERIOD_COLUMN_WIDTH = 100;

export const RESEARCH_DEVELOPMENT_ROW_SPECS: ResearchDevelopmentRowSpec[] = [
  {
    key: 'rd-depreciation',
    label: ['減価償却(含むのれん償却)', '(対売上高比率)', 'のれん償却'],
    cells: [
      { field: 'depreciationAmortization', kind: 'amount' },
      { field: 'depRevenueRatio', kind: 'ratio' },
      { field: 'amortization', kind: 'amount' },
    ],
  },
  {
    key: 'rd-capex',
    label: ['設備投資', '(対売上高比率)'],
    cells: [
      { field: 'capex', kind: 'amount' },
      { field: 'capexRevenueRatio', kind: 'ratio' },
    ],
  },
  {
    key: 'rd-research',
    label: ['研究開発費', '(対売上高比率)'],
    cells: [
      { field: 'rdCost', kind: 'amount' },
      { field: 'rdRevenueRatio', kind: 'ratio' },
    ],
  },
];

const RESEARCH_DEVELOPMENT_CHART_SPECS = [
  {
    key: 'depreciation',
    label: '減価償却（含むのれん償却）',
    color: FINANCIAL_COLORS.red,
    field: 'depreciationAmortization' as const,
  },
  {
    key: 'capex',
    label: '設備投資',
    color: FINANCIAL_COLORS.blue,
    field: 'capex' as const,
  },
  {
    key: 'research',
    label: '研究開発費',
    color: FINANCIAL_COLORS.purple,
    field: 'rdCost' as const,
  },
];

const numericResearchDevelopmentField = (
  row: ResearchDevelopmentApiItem,
  field: ResearchDevelopmentValueField,
): number | null => numericField(row, field);

const hasResearchDevelopmentDisplayValue = (
  row: ResearchDevelopmentApiItem,
): boolean =>
  RESEARCH_DEVELOPMENT_ROW_SPECS.some((spec) => (
    spec.cells.some((cell) => numericResearchDevelopmentField(row, cell.field) !== null)
  ));

const buildCompatibleResearchDevelopmentUnitLabel = (
  rows: ResearchDevelopmentApiItem[],
): string | undefined => {
  const valueRows = rows.filter(hasResearchDevelopmentDisplayValue);
  if (valueRows.length === 0) return undefined;

  const firstUnit = normalizeMetadataValue(valueRows[0].unit);
  const firstCurrency = normalizeMetadataValue(valueRows[0].currency);
  if (!firstUnit || !firstCurrency) return undefined;

  const compatible = valueRows.every((row) => (
    normalizeMetadataValue(row.unit) === firstUnit &&
    normalizeMetadataValue(row.currency) === firstCurrency
  ));
  if (!compatible) return undefined;

  const scale = UNIT_SCALE_LABELS[firstUnit] ?? firstUnit;
  const currency = CURRENCY_LABELS[firstCurrency] ?? firstCurrency;
  const text = `${scale}${currency}`;
  return text ? `${UNIT_LABEL_PREFIX}${text}` : undefined;
};

const buildResearchDevelopmentChart = (
  rows: ResearchDevelopmentApiItem[],
  chartKey: string,
): { chart: GroupedFinancialChartData; series: FinancialSeries[] } => {
  const series: FinancialSeries[] = RESEARCH_DEVELOPMENT_CHART_SPECS.map((spec) => ({
    key: spec.key,
    label: spec.label,
    color: spec.color,
    // work_items_93: the chart compares spending magnitudes. Preserve signed
    // API values for the table, but plot every finite chart value positively.
    values: rows.map((row) => {
      const value = numericResearchDevelopmentField(row, spec.field);
      return value === null ? null : Math.abs(value);
    }),
  }));
  const { max, interval } = buildAxisScale(series.flatMap((s) => s.values));

  return {
    series,
    chart: {
      key: chartKey,
      labels: rows.map((row) => formatPeriodChartLabel(row.periodEndDate)),
      yAxisMin: 0,
      yAxisMax: max,
      yAxisInterval: interval,
      series,
    },
  };
};

const buildResearchDevelopmentColumns = (
  rows: ResearchDevelopmentApiItem[],
): MatrixTableColumn[] => {
  const unitLabel = buildCompatibleResearchDevelopmentUnitLabel(rows);
  const labelColumn: MatrixTableColumn = {
    key: 'label',
    label: unitLabel ? [line('決算期'), line(unitLabel)] : [line('決算期')],
    align: 'left',
  };

  return [
    labelColumn,
    ...rows.map<MatrixTableColumn>((row, index) => {
      const period = row.periodEndDate?.trim() || PLACEHOLDER;
      return {
        key: `period-${periodSortKey(row.periodEndDate) || index}`,
        label: [line(period)],
        width: RESEARCH_DEVELOPMENT_PERIOD_COLUMN_WIDTH,
        align: 'right',
      };
    }),
  ];
};

const buildResearchDevelopmentCellLine = (
  row: ResearchDevelopmentApiItem,
  spec: ResearchDevelopmentCellLineSpec,
): DisplayLine => {
  const value = numericResearchDevelopmentField(row, spec.field);
  return line(
    spec.kind === 'ratio'
      ? formatPercent(value, RATIO_FRACTION_DIGITS)
      : formatNumber(value, AMOUNT_FRACTION_DIGITS),
  );
};

const buildResearchDevelopmentRows = (
  rows: ResearchDevelopmentApiItem[],
): MatrixTableRow[] =>
  RESEARCH_DEVELOPMENT_ROW_SPECS.map((spec) => ({
    key: spec.key,
    label: spec.label.map((labelText) => line(labelText)),
    cells: rows.map((row) => (
      spec.cells.map((cell) => buildResearchDevelopmentCellLine(row, cell))
    )),
  }));

/**
 * Maps GET /api/fundamentals/research-development onto the R&D `FinancialSection`.
 *
 * `fallback` supplies static presentation metadata only. Financial values,
 * period labels and chart scale come from `apiData`; missing data renders an
 * empty no-data section with static metric rows instead of mock numbers.
 */
export const mapResearchDevelopmentApiToFinancialSection = (
  apiData: ResearchDevelopmentApiItem[] | null | undefined,
  fallback: FinancialSection,
): FinancialSection => {
  const rows = sortByPeriodAscending(Array.isArray(apiData) ? apiData : []);
  const { chart, series } = buildResearchDevelopmentChart(rows, fallback.chart.key);
  const labelColumnWidth = fallback.table.labelColumnWidth ?? 0;
  const minWidth =
    labelColumnWidth + rows.length * RESEARCH_DEVELOPMENT_PERIOD_COLUMN_WIDTH;

  return {
    ...fallback,
    legend: series,
    chart,
    table: {
      ...fallback.table,
      minWidth,
      columns: buildResearchDevelopmentColumns(rows),
      rows: buildResearchDevelopmentRows(rows),
    },
  };
};

interface QuarterlyTableSpec {
  key: string;
  label: string;
  valueField: FullYearValueField;
  yoyField: FullYearYoyField;
}

interface QuarterlyChartSpec {
  key: string;
  label: string;
  valueField: Extract<
    FullYearValueField,
    'revenue' | 'operatingProfit' | 'recurringProfit' | 'netProfit'
  >;
}

const QUARTERLY_TABLE_SPECS: QuarterlyTableSpec[] = [
  { key: 'revenue', label: '売上高', valueField: 'revenue', yoyField: 'revenueYoy' },
  {
    key: 'operating',
    label: '営業利益',
    valueField: 'operatingProfit',
    yoyField: 'operatingProfitYoy',
  },
  {
    key: 'pretax',
    label: '税引前利益',
    valueField: 'recurringProfit',
    yoyField: 'recurringProfitYoy',
  },
  { key: 'net', label: '最終利益', valueField: 'netProfit', yoyField: 'netProfitYoy' },
  { key: 'eps', label: 'EPS', valueField: 'eps', yoyField: 'epsYoy' },
  { key: 'bps', label: 'BPS', valueField: 'bps', yoyField: 'bpsYoy' },
];

const QUARTERLY_CHART_SPECS: QuarterlyChartSpec[] = [
  { key: 'revenue', label: '売上高', valueField: 'revenue' },
  { key: 'operating', label: '営業利益', valueField: 'operatingProfit' },
  { key: 'pretax', label: '税引前利益', valueField: 'recurringProfit' },
  { key: 'net', label: '最終利益', valueField: 'netProfit' },
];

export const QUARTER_KEYS: QuarterKey[] = ['Q1', 'Q2', 'Q3', 'Q4'];

interface QuarterlyFiscalYearGroup {
  quarters: Partial<Record<QuarterKey, QuarterlyResultsApiItem>>;
}

const normalizeQuarterKey = (
  value: string | null | undefined,
): QuarterKey | null => {
  const normalized = value?.trim().toUpperCase();
  return QUARTER_KEYS.includes(normalized as QuarterKey)
    ? normalized as QuarterKey
    : null;
};

const quarterlySourcePriority = (
  row: QuarterlyResultsApiItem,
  forecastType: QuarterlyResultsForecastType,
): number => {
  const source = normalizeSource(row.source);
  if (source === 'std') return 2;
  if (source === forecastType) return 1;
  return 0;
};

const shouldReplaceQuarter = (
  current: QuarterlyResultsApiItem,
  candidate: QuarterlyResultsApiItem,
  forecastType: QuarterlyResultsForecastType,
): boolean => {
  const currentPriority = quarterlySourcePriority(current, forecastType);
  const candidatePriority = quarterlySourcePriority(candidate, forecastType);
  if (candidatePriority !== currentPriority) {
    return candidatePriority > currentPriority;
  }

  // Rows are processed in ascending date and stable input order. For duplicate
  // rows from the same source, consistently retain the last supplied record.
  return true;
};

const groupQuarterlyRows = (
  rows: QuarterlyResultsApiItem[],
  forecastType: QuarterlyResultsForecastType,
): QuarterlyFiscalYearGroup[] => {
  const selectedRows = sortByPeriodAscending(rows.filter((row) => {
    const source = normalizeSource(row.source);
    return normalizeQuarterKey(row.quarterLabel) !== null &&
      (source === 'std' || source === forecastType);
  }));
  const groups: QuarterlyFiscalYearGroup[] = [];
  let currentGroup: QuarterlyFiscalYearGroup | null = null;

  selectedRows.forEach((row) => {
    const quarter = normalizeQuarterKey(row.quarterLabel);
    if (!quarter) return;

    if (quarter === 'Q1') {
      const existingQ1 = currentGroup?.quarters.Q1;
      const sameQ1Period = Boolean(
        existingQ1 &&
        periodSortKey(existingQ1.periodEndDate) === periodSortKey(row.periodEndDate),
      );

      if (!currentGroup || !sameQ1Period) {
        currentGroup = { quarters: {} };
        groups.push(currentGroup);
      }
    } else if (!currentGroup) {
      // Preserve an initial Q2-Q4 partial group. It naturally falls out when
      // enough newer fiscal-year groups are selected.
      currentGroup = { quarters: {} };
      groups.push(currentGroup);
    }

    const existing = currentGroup.quarters[quarter];
    if (!existing || shouldReplaceQuarter(existing, row, forecastType)) {
      currentGroup.quarters[quarter] = row;
    }
  });

  return groups;
};

const flattenQuarterlyGroups = (
  groups: QuarterlyFiscalYearGroup[],
): QuarterlyResultsApiItem[] => groups.flatMap((group) => (
  QUARTER_KEYS.flatMap((quarter) => {
    const row = group.quarters[quarter];
    return row ? [row] : [];
  })
));

const buildQuarterlyUnitLabel = (
  rows: QuarterlyResultsApiItem[],
): string | undefined => buildCompatibleFullYearUnitLabel([
  ...rows.filter((row) => normalizeSource(row.source) === 'std'),
  ...rows.filter((row) => normalizeSource(row.source) !== 'std'),
]);

const buildQuarterlyColumns = (
  fallback: QuarterlyPerformanceSectionData,
  rows: QuarterlyResultsApiItem[],
): MatrixTableColumn[] => {
  const unitLabel = buildQuarterlyUnitLabel(rows);
  const fallbackLabelColumn = fallback.table.columns[0];
  const labelColumn: MatrixTableColumn = fallbackLabelColumn ?? {
    key: 'label',
    label: [line('決算期')],
    align: 'left',
  };

  return [
    {
      ...labelColumn,
      key: 'label',
      label: unitLabel
        ? [line('決算期'), line(unitLabel)]
        : [line('決算期')],
      align: 'left',
    },
    ...QUARTERLY_TABLE_SPECS.map((spec, index) => {
      const fallbackColumn = fallback.table.columns[index + 1];
      return {
        ...fallbackColumn,
        key: spec.key,
        label: [line(spec.label)],
        align: fallbackColumn?.align ?? 'right',
      };
    }),
  ];
};

const buildQuarterlyCell = (
  row: QuarterlyResultsApiItem,
  spec: QuarterlyTableSpec,
): DisplayLine[] => [
  line(formatNumber(numericFullYearField(row, spec.valueField), AMOUNT_FRACTION_DIGITS)),
  buildFullYearYoyLine(numericFullYearField(row, spec.yoyField)),
];

const buildQuarterlyRows = (
  rows: QuarterlyResultsApiItem[],
): MatrixTableRow[] => rows.map((row, index) => {
  const isForecast = normalizeSource(row.source) !== 'std';
  const period = withForecastMarker(
    formatPeriodMonthLabel(row.periodEndDate),
    isForecast,
  );

  return {
    key: `quarterly-${normalizeSource(row.source) || 'unknown'}-${periodSortKey(row.periodEndDate) || index}`,
    label: [line(period), line('(前年比)')],
    cells: QUARTERLY_TABLE_SPECS.map((spec) => buildQuarterlyCell(row, spec)),
  };
});

const buildQuarterlyPoint = (
  row: QuarterlyResultsApiItem,
  valueField: QuarterlyChartSpec['valueField'],
): QuarterlyPeriodPoint => ({
  key: `${periodSortKey(row.periodEndDate)}-${normalizeQuarterKey(row.quarterLabel)}`,
  label: formatPeriodMonthLabel(row.periodEndDate),
  periodEndDate: row.periodEndDate?.trim() ?? '',
  // Only rows with a recognized quarter reach the selected groups.
  quarter: normalizeQuarterKey(row.quarterLabel) as QuarterKey,
  isForecast: normalizeSource(row.source) !== 'std',
  value: numericFullYearField(row, valueField),
});

const buildQuarterlyCharts = (
  rows: QuarterlyResultsApiItem[],
  fallback: QuarterlyPerformanceSectionData,
): QuarterlyPeriodChartData[] => {
  const unitLabel = buildQuarterlyUnitLabel(rows);

  return QUARTERLY_CHART_SPECS.map((spec, index) => {
    const fallbackChart: QuarterlyPeriodChartData = fallback.charts[index] ?? {
      key: `quarterly-${spec.key}`,
      title: spec.label,
      unit: '',
      yAxisMin: 0,
      yAxisMax: FALLBACK_AXIS_MAX,
      yAxisInterval: FALLBACK_AXIS_MAX / AXIS_TICK_COUNT,
      points: [],
    };
    const points = rows.map((row) => buildQuarterlyPoint(row, spec.valueField));
    const values = points.map((point) => point.value);
    const { min, max, interval } = values.some((value) => value !== null && value < 0)
      ? buildSignedAxisScale(values)
      : { min: 0, ...buildAxisScale(values) };

    return {
      ...fallbackChart,
      key: fallbackChart.key || `quarterly-${spec.key}`,
      title: buildFullYearChartTitle(spec.label, unitLabel),
      unit: unitTextFromLabel(unitLabel) ?? '',
      yAxisMin: min,
      yAxisMax: max,
      yAxisInterval: interval,
      points,
    };
  });
};

/**
 * Maps GET /api/fundamentals/quarterly-results onto the Quarterly performance
 * history section. Fallback data supplies presentation metadata only.
 */
export const mapQuarterlyResultsApiToPerformanceHistorySection = (
  apiData: QuarterlyResultsApiItem[] | null | undefined,
  fallback: QuarterlyPerformanceSectionData,
  options: {
    forecastType: QuarterlyResultsForecastType;
    displayPeriodCount: number;
  },
): QuarterlyPerformanceSectionData => {
  const rows = Array.isArray(apiData) ? apiData : [];
  const displayCount = toDisplayCount(options.displayPeriodCount);
  const fiscalYearGroups = groupQuarterlyRows(rows, options.forecastType);
  const selectedGroups = displayCount > 0
    ? fiscalYearGroups.slice(-displayCount)
    : [];
  const selectedRows = sortByPeriodAscending(flattenQuarterlyGroups(selectedGroups));
  const tableRows = sortByPeriodAscending(selectedRows).reverse();

  return {
    ...fallback,
    charts: buildQuarterlyCharts(selectedRows, fallback),
    table: {
      ...fallback.table,
      columns: buildQuarterlyColumns(fallback, selectedRows),
      rows: buildQuarterlyRows(tableRows),
    },
  };
};
